import { useState } from 'react';
import { API_URL, API_VERSION } from '@constants';
import { paths } from '../../types/api';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';
import { useData } from '@hooks/useData';

const ENDPOINT_URL = '/shp-shipping/cancel-locker-compartment-reservation';

const ENDPOINT_URL_WITH_VERSION = `${API_VERSION}${ENDPOINT_URL}` as const;
type url = typeof ENDPOINT_URL_WITH_VERSION;

export type CancelLockerCompartmentBookingInput = paths[url]['post']['requestBody']['content']['application/json'];

/**
 * Requests endpoint to cancel a locker compartment booking.
 */
export const useCancelLockerCompartmentReservation = () => {
    const { getHeaders, handleCommonErrors } = useApiConfig();
    const [lastResponseTime, setLastResponseTime] = useState<Date | null>(null);
    const { setIsLoadingCancelLockerCompartmentReservation } = useData();

    // general error message (for general errors, example: 500)
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    // for errors associated with form fields (422 Unprocessable Content)
    const [validationErrors, setValidationErrors] = useState<Record<string, string[]> | null>(null);

    const request = async (props: CancelLockerCompartmentBookingInput) => {
        setIsLoadingCancelLockerCompartmentReservation(true);
        setErrorMessage(null);
        setValidationErrors(null);

        try {
            const response = await fetch(`${API_URL}${ENDPOINT_URL}`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({ ...props }),
            });

            if (!response.ok) {
                const error = new Error() as ApiError;

                try {
                    const json = await response.json();
                    error.message = json.message;
                    error.errors = json.errors;
                } catch (error: any) {
                    error.message = '';
                }
                
                error.statusCode = response.status;
                throw error;
            }
            
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            handleCommonErrors(error);
            error.message && setErrorMessage(error.message);
            error.errors && setValidationErrors(error.errors);
        } finally {
            setIsLoadingCancelLockerCompartmentReservation(false);
            setLastResponseTime(new Date());
        }
    };

    return { request, errorMessage, validationErrors, lastResponseTime };
};
